<script setup lang="ts">
/*
  The header bar of a routed page: a back arrow, the page's name, pills beside it, a
  search with room of its own, and right-aligned actions.

  `DesignerHeader`'s anatomy without the designer's parts. That one also mounts the
  build button, the background-task indicator and the dependency notice — live project
  state a dataspace or an account page has none of. Everything else is the same, down
  to the classes, so a designer page and an operator page read as one product.
*/
import { useRouter } from "vue-router";
import { mdiArrowLeft } from "@mdi/js";
import { HabButton } from "@livereader/habitus-vue";

withDefaults(
	defineProps<{
		/** Route to return to. The back arrow is omitted without one. */
		backTo?: string;
	}>(),
	{ backTo: "" },
);

defineSlots<{
	/** The page's own name. */
	title(): unknown;
	/** Pills beside the title — a version, your role. */
	"title-append"(): unknown;
	/** A search field, given room of its own. */
	search(): unknown;
	/** Right-aligned actions: one primary, the rest outlined or text. */
	actions(): unknown;
}>();

const router = useRouter();
</script>

<template>
	<!--
		No outer margin: a header cannot know what follows it, and a caller that sticks
		it to the top of a scroller needs the gap inside the sticky box, not after it.
	-->
	<header class="flex flex-wrap items-center gap-4 gap-y-2 px-1 py-1 font-sans">
		<HabButton
			v-if="backTo"
			icon-only
			variant="text"
			tone="neutral"
			size="lg"
			:icon="mdiArrowLeft"
			:aria-label="$t('common.actions.back')"
			@click="router.push(backTo)"
		/>

		<!-- `text-page` is the 21px the design source gives a routed view's own name. -->
		<h1 class="min-w-0 flex-[0_1_auto] truncate text-page font-medium tracking-tight">
			<slot name="title" />
		</h1>

		<slot name="title-append" />

		<!-- 3rem clear of the title, and allowed to shrink before the title does. -->
		<div
			v-if="$slots.search"
			class="ml-8 flex min-w-0 flex-[0_1_32rem] items-center gap-3 max-medium:ml-4"
		>
			<slot name="search" />
		</div>

		<div
			v-if="$slots.actions"
			class="ml-auto flex items-center gap-2"
		>
			<slot name="actions" />
		</div>
	</header>
</template>
